import React, { useState, useEffect } from 'react';
import './Auditlogs.css';
import { api } from '../../utils/authApi';  

const AuditLogs = () => {
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('ALL');
  const [searchEmail, setSearchEmail] = useState('');
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Fetch audit logs from API
  useEffect(() => {
    fetchAudits();
  }, []);

  const fetchAudits = async () => {
    try {
      const response = await api.get('/audit/');      
      const data = await response.json();
      // Transform the array data into objects
      const transformedAudits = data.Message.map(audit => ({
        id: audit[0],
        user_email: audit[1],
        user_id: audit[2],
        action_type: audit[3],
        previous_state: audit[4],
        new_state: audit[5],
        action_time: audit[6]
      }));
      
      setAudits(transformedAudits);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching audits:', error);
      setLoading(false);
    }
  };

  // Filter audits based on action type and email search
  const filteredAudits = audits.filter(audit => {
    const matchesType = filterType === 'ALL' || audit.action_type === filterType;
    const matchesEmail = audit.user_email.toLowerCase().includes(searchEmail.toLowerCase());
    return matchesType && matchesEmail;
  });

  // Format date nicely
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get badge color based on action type
  const getActionBadgeClass = (actionType) => {
    const badgeMap = {
      'ENGINEER_LOGIN': 'badge-info',
      'CONCERN_UPDATED': 'badge-warning',
      'CONCERN_ADDED': 'badge-success',
      'CONCERN_DELETED': 'badge-danger',
      'CLIENT_MADE': 'badge-primary',
      'PROJECT_ADDED': 'badge-secondary'
    };
    return badgeMap[actionType] || 'badge-default';
  };

  // Format action type for display
  const formatActionType = (actionType) => {
    return actionType.replace(/_/g, ' ');
  };

  // Open modal to view details
  const viewDetails = (audit) => {
    setSelectedAudit(audit);
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedAudit(null);
  };

  if (loading) {
    return (
      <div className="audit-container">
        <div className="loading">Loading audit logs...</div>
      </div>
    );
  }

  return (
    <div className="audit-container">
      <div className="audit-header">
        <h1>Audit Logs</h1>
        <p className="subtitle">System activity tracking and monitoring</p>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Action Type:</label>
          <select 
            value={filterType} 
            onChange={(e) => setFilterType(e.target.value)}
            className="filter-select"
          >
            <option value="ALL">All Actions</option>
            <option value="ENGINEER_LOGIN">Engineer Login</option>
            <option value="CONCERN_UPDATED">Concern Updated</option>
            <option value="CONCERN_ADDED">Concern Added</option>
            <option value="CONCERN_DELETED">Concern Deleted</option>
            <option value="CLIENT_MADE">Client Created</option>
            <option value="PROJECT_ADDED">Project Added</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Search by Email:</label>
          <input
            type="text"
            placeholder="Enter email..."
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            className="filter-input"
          />
        </div>

        <div className="results-count">
          Showing {filteredAudits.length} of {audits.length} logs
        </div>
      </div>

      {/* Audit Table */}
      <div className="audit-table-container">
        <table className="audit-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>User</th>
              <th>Action</th>
              <th>Timestamp</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {filteredAudits.length === 0 ? (
              <tr>
                <td colSpan="5" className="no-results">
                  No audit logs found matching your filters
                </td>
              </tr>
            ) : (
              filteredAudits.map((audit) => (
                <tr key={audit.id}>
                  <td className="audit-id">#{audit.id}</td>
                  <td className="audit-user">
                    <div className="user-info">
                      <span className="user-email">{audit.user_email}</span>
                      <span className="user-id">ID: {audit.user_id}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`action-badge ${getActionBadgeClass(audit.action_type)}`}>
                      {formatActionType(audit.action_type)}
                    </span>
                  </td>
                  <td className="audit-time">{formatDate(audit.action_time)}</td>
                  <td>
                    <button 
                      className="view-details-btn"
                      onClick={() => viewDetails(audit)}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Details Modal */}
      {showModal && selectedAudit && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Audit Log Details</h2>
              <button className="close-btn" onClick={closeModal}>&times;</button>
            </div>
            
            <div className="modal-body">
              <div className="detail-section">
                <h3>General Information</h3>
                <div className="detail-row">
                  <span className="detail-label">Audit ID:</span>
                  <span className="detail-value">#{selectedAudit.id}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">User Email:</span>
                  <span className="detail-value">{selectedAudit.user_email}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">User ID:</span>
                  <span className="detail-value">{selectedAudit.user_id}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Action Type:</span>
                  <span className={`action-badge ${getActionBadgeClass(selectedAudit.action_type)}`}>
                    {formatActionType(selectedAudit.action_type)}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Timestamp:</span>
                  <span className="detail-value">{formatDate(selectedAudit.action_time)}</span>
                </div>
              </div>

              {selectedAudit.previous_state && (
                <div className="detail-section">
                  <h3>Previous State</h3>
                  <pre className="json-display">
                    {JSON.stringify(selectedAudit.previous_state, null, 2)}
                  </pre>
                </div>
              )}

              {selectedAudit.new_state && (
                <div className="detail-section">
                  <h3>New State</h3>
                  <pre className="json-display">
                    {JSON.stringify(selectedAudit.new_state, null, 2)}
                  </pre>
                </div>
              )}

              {!selectedAudit.previous_state && !selectedAudit.new_state && (
                <div className="detail-section">
                  <p className="no-state-info">No state changes recorded for this action.</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="close-modal-btn" onClick={closeModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;